import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { computePosition, flip, offset, Placement, shift } from '@floating-ui/dom';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Subscription } from 'rxjs';
import { generateId } from '../../../_core/utils/generate-id';

@Directive({
  selector: '[opacTooltip]'
})
export class TooltipDirective implements AfterViewInit, OnDestroy {
  private readonly target = inject(ElementRef)
  private readonly focusMonitor = inject(FocusMonitor);

  private container: HTMLElement;
  private isActive = false;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  private focusMonitorSubscription: Subscription;
  private mouseEnterHandler = this.onMouseEnter.bind(this);
  private mouseLeaveHandler = this.onMouseLeave.bind(this);

  /**
   * Text content of the tooltip
   */
  @Input('opacTooltip') content: string;
  /**
   * Determines if tooltip must be disabled
   */
  @Input('tooltipDisabled') isDisabled = false;
  /**
   * Position of the tooltip placement. Defaults to 'bottom'
   */
  @Input() position: Placement = 'bottom';
  /**
   * Delay before showing the tooltip. Defaults to 0
   */
  @Input() showDelay = 0;
  /**
   * Delay before hiding the tooltip. Defaults to 0
   */
  @Input() hideDelay = 0;

  ngAfterViewInit() {
    this.target.nativeElement.addEventListener('mouseenter', this.mouseEnterHandler);
    this.target.nativeElement.addEventListener('mouseleave', this.mouseLeaveHandler);

    this.focusMonitorSubscription = this.focusMonitor.monitor(this.target.nativeElement).subscribe((origin) => {
      if (origin === 'program') {
        return;
      }

      if (origin === null) { // blur
        this.deactivate();
      } else {
        this.activate();
      }
    });
  }

  ngOnDestroy() {
    this.removeEventListeners();
    this.deactivate();
  }

  private removeEventListeners() {
    this.target.nativeElement.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.target.nativeElement.removeEventListener('mouseleave', this.mouseLeaveHandler);
    this.focusMonitorSubscription?.unsubscribe();
  }

  private onMouseEnter() {
    this.activate();
  }

  private onMouseLeave() {
    this.deactivate();
  }

  private activate() {
    if (this.isActive || this.isDisabled) {
      return;
    }

    this.isActive = true;

    if (this.container) {
      this.clearHideTimeout();
      return;
    }

    if (this.showDelay > 0) {
      this.showTimeout = setTimeout(() => {
        this.show();
      }, this.showDelay);
    } else {
      this.show();
    }
  }

  private async show() {
    if (!this.content) {
      return;
    }

    this.create();
    this.align();
    this.fadeIn(250);
  }

  private create() {
    this.container = document.createElement('div');
    this.container.setAttribute('id', generateId());
    this.container.setAttribute('role', 'tooltip');
    this.container.setAttribute('aria-hidden', 'true');

    this.container.innerHTML = this.content;

    document.body.appendChild(this.container);
  }

  private deactivate() {
    if (!this.isActive) {
      return;
    }

    this.clearShowTimeout();
    this.isActive = false;

    if (this.hideDelay > 0) {
      this.hideTimeout = setTimeout(() => {
        this.remove();
      }, this.hideDelay);
    } else {
      this.remove();
    }
  }

  private async remove() {
    if (this.container) {
       this.fadeOut(150).then(() => {

         // If mouse entered again after fadeOut, tooltip must be shown, so don't destroy it
         if (!this.isActive) {
           document.body.removeChild(this.container);
           this.clearTimeouts();
           this.container = null;
         } else {
           this.fadeIn(150);
         }
       });
    }
  }

  private align() {
    computePosition(this.target.nativeElement, this.container, {
      placement: this.position,
      strategy: 'absolute',
      middleware: [
        flip({
          boundary: {
            x: 0,
            y: 100,
            width: document.documentElement.clientWidth,
            height: document.documentElement.clientHeight - 100
          }
        }),
        shift(),
        offset(4),
      ],
    }).then(({ x, y }) => {
      this.container.style.left = `${x}px`;
      this.container.style.top = `${y}px`;
    });
  }

  private fadeIn(duration: number): Promise<Animation> {
    const keyframes: Keyframe[] = [
      {
        opacity: 0,
      },
      {
        opacity: 1,
      }
    ];
    const options: KeyframeAnimationOptions = {
      duration,
      fill: 'forwards',
      id: 'opac-tooltip-fade-in',
    };

    return this.container.animate(keyframes, options).finished;
  }

  private fadeOut(duration: number): Promise<Animation> {
    const keyframes: Keyframe[] = [
      {
        opacity: 1,
      },
      {
        opacity: 0,
      }
    ];
    const options: KeyframeAnimationOptions = {
      duration,
      fill: 'forwards',
      id: 'opac-tooltip-fade-out',
    };

    return this.container.animate(keyframes, options).finished;
  }

  private clearShowTimeout() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  private clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private clearTimeouts() {
    this.clearShowTimeout();
    this.clearHideTimeout();
  }
}
